import { useMemo } from "react";
import type { TFunction } from "i18next";
import type { U2000FormStateHandlers } from "../../../../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import { U2000FormStateEnumState } from "../../../../../u2000Core/reducer/formReducer/U2000FormState";
import U2000Panel from "../../../../../u2000Core/components/layout/U2000Panel";
import U2000CheckBox from "../../../../../u2000Core/components/formControls/checkbox/U2000CheckBox";
import U2000ComboBox from "../../../../../u2000Core/components/formControls/combobox/U2000ComboBox";
import U2000InputCurrency from "../../../../../u2000Core/components/formControls/input/U2000InputCurrency";
import U2000InputDecimal from "../../../../../u2000Core/components/formControls/input/U2000InputDecimal";
import U2000InputCalendarCustom from "../../../../../u2000Core/components/formControls/input/U2000InputCalendarCustom";
import U2000InputText from "../../../../../u2000Core/components/formControls/input/U2000InputText";
import { FaExclamationTriangle } from "react-icons/fa";
import { NS } from "../../../../U3121";
import type { U3121BillingFrequency, U3121SubProject } from "../../../../U3121Service";
import { parseU3121DefaultSellingRateNumber, type U3121InitLUPersonalization } from "../../../../U3121Service";

interface U3121SubProjectBillingProfileBodyProps {
  formStateHandlers: U2000FormStateHandlers<U3121SubProject>;
  t: TFunction;
  personalization?: U3121InitLUPersonalization | null;
  onValidateAfterBillingChange?: (data?: U3121SubProject) => void;
}

/** Clone et met à jour billingProfile.billable uniquement (BL-0044). */
function setBillableExclusive(formStateHandlers: U2000FormStateHandlers<U3121SubProject>, billableValue: boolean): U3121SubProject | undefined {
  const data = formStateHandlers.getData();
  if (!data) return undefined;
  const next = JSON.parse(JSON.stringify(data)) as U3121SubProject;
  if (!next.billingProfile) next.billingProfile = { id: "" };
  next.billingProfile.billable = billableValue;
  formStateHandlers.setData(next);
  return next;
}

function setFixedPriceHourlyExclusive(
  formStateHandlers: U2000FormStateHandlers<U3121SubProject>,
  fixedPriceValue: boolean,
  hourlyValue: boolean,
  defaultSellingRate?: number,
): U3121SubProject | undefined {
  const data = formStateHandlers.getData();
  if (!data) return undefined;
  const next = JSON.parse(JSON.stringify(data)) as U3121SubProject;
  if (!next.billingProfile) next.billingProfile = { id: "" };
  next.billingProfile.fixedPrice = fixedPriceValue;
  next.billingProfile.hourly = hourlyValue;
  if (hourlyValue) {
    next.billingProfile.fixedAmount = 0;
    if (next.billingProfile.sellingRate == null || next.billingProfile.sellingRate === undefined) {
      if (defaultSellingRate != null) next.billingProfile.sellingRate = defaultSellingRate;
    }
  } else {
    next.billingProfile.sellingRate = null;
  }
  formStateHandlers.setData(next);
  return next;
}

function U3121SubProjectBillingProfileBody({ formStateHandlers, t, personalization, onValidateAfterBillingChange }: U3121SubProjectBillingProfileBodyProps) {
  const defaultSellingRate = parseU3121DefaultSellingRateNumber(personalization ?? undefined);
  const data = formStateHandlers.getData();
  const billable = data?.billingProfile?.billable ?? false;
  const nonBillable = !billable;
  const fixedPrice = Boolean(data?.billingProfile?.fixedPrice);
  const hourly = Boolean(data?.billingProfile?.hourly);
  const billingFrequency: U3121BillingFrequency | undefined = data?.billingProfile?.billingFrequency;
  const isByStep = billingFrequency === "ByStep";

  const billingFrequencyOptions: { value: U3121BillingFrequency; label: string }[] = useMemo(
    () => [
      { value: "EndProject", label: t("billingFrequencyEndProject", { ns: NS }) },
      { value: "ByStep", label: t("billingFrequencyByStep", { ns: NS }) },
    ],
    [t],
  );
  const errors = formStateHandlers.getErrors();
  const hasPanelFixedAmountError = Boolean(errors["billingProfile.fixedAmount"]);
  const hasPanelHourlyMarkupError = Boolean(errors["billingProfile.hourlyMarkup"]);
  const hasPanelSellingRateError = Boolean(errors["billingProfile.sellingRate"]);
  const showBillableModeError = billable && !hourly && !fixedPrice;
  const panelErrorClassName = showBillableModeError ? "border-red-500! border-2" : "";
  const fixedAmountPanelErrorClassName = hasPanelFixedAmountError ? "border-red-500! border-2" : "";
  const hourlyPanelErrorClassName = hasPanelSellingRateError ? "border-red-500! border-2" : "";
  const hourlyMarkupFieldErrorClassName = hasPanelHourlyMarkupError ? "border-red-500! border-2" : "";
  const billableModeMessage = t("errBillableRequiresHoraireOrForfaitaire", { ns: NS });

  const formHandlersBillable: U2000FormStateHandlers<U3121SubProject> = {
    ...formStateHandlers,
    onChange: (e: React.ChangeEvent<HTMLInputElement>, value: string | number | boolean) => {
      const id = e?.target?.id;
      if (id === "billingProfile.billable") {
        const next = setBillableExclusive(formStateHandlers, value === true);
        setTimeout(() => onValidateAfterBillingChange?.(next), 0);
        return;
      }
      formStateHandlers.onChange?.(e, value);
    },
  };

  const formHandlersFixedPriceHourly: U2000FormStateHandlers<U3121SubProject> = {
    ...formStateHandlers,
    onChange: (e: React.ChangeEvent<HTMLInputElement>, value: string | number | boolean) => {
      const id = e?.target?.id;
      if (id === "billingProfile.fixedPrice" && value === true) {
        const next = setFixedPriceHourlyExclusive(formStateHandlers, true, false, defaultSellingRate);
        formStateHandlers.onChange?.(e, value);
        setTimeout(() => onValidateAfterBillingChange?.(next), 0);
        return;
      }
      if (id === "billingProfile.fixedPrice" && value === false) {
        const next = setFixedPriceHourlyExclusive(formStateHandlers, false, false, defaultSellingRate);
        formStateHandlers.onChange?.(e, value);
        setTimeout(() => onValidateAfterBillingChange?.(next), 0);
        return;
      }
      if (id === "billingProfile.hourly" && value === true) {
        const next = setFixedPriceHourlyExclusive(formStateHandlers, false, true, defaultSellingRate);
        formStateHandlers.onChange?.(e, value);
        setTimeout(() => onValidateAfterBillingChange?.(next), 0);
        return;
      }
      if (id === "billingProfile.hourly" && value === false) {
        const next = setFixedPriceHourlyExclusive(formStateHandlers, false, false, defaultSellingRate);
        formStateHandlers.onChange?.(e, value);
        setTimeout(() => onValidateAfterBillingChange?.(next), 0);
        return;
      }
      formStateHandlers.onChange?.(e, value);
    },
  };

  const formHandlersBillingFrequency: U2000FormStateHandlers<U3121SubProject> = {
    ...formStateHandlers,
    onChange: (e: React.ChangeEvent<HTMLInputElement>, value: string | number | boolean) => {
      const id = e?.target?.id;
      if (id === "billingProfile.billingFrequency" && value === "EndProject") {
        const current = formStateHandlers.getData();
        if (current?.billingProfile) {
          const next: U3121SubProject = {
            ...current,
            billingProfile: { ...current.billingProfile, billingFrequency: "EndProject", billingFrequencyNote: "" },
          };
          formStateHandlers.setData(next);
          setTimeout(() => onValidateAfterBillingChange?.(next), 0);
          return;
        }
      }
      formStateHandlers.onChange?.(e, value);
    },
  };

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      <div className="col-span-full mb-4 flex w-full flex-wrap items-center gap-x-6 gap-y-3 border-b border-gray-200 pb-3">
        <U2000CheckBox formStateHandlers={formHandlersBillable} fieldName="billingProfile.billable" label={t("lblBillable", { ns: NS })} />
        <label
          className={`flex items-center gap-2 text-sm font-medium ${formStateHandlers.getFormState?.() === U2000FormStateEnumState.READ ? "cursor-default text-gray-500" : "cursor-pointer text-gray-700"}`}
        >
          <input
            type="checkbox"
            id="billingProfile.nonBillable"
            checked={nonBillable}
            onChange={(e) => {
              const next = setBillableExclusive(formStateHandlers, !e.target.checked);
              setTimeout(() => onValidateAfterBillingChange?.(next), 0);
            }}
            disabled={formStateHandlers.getFormState?.() === U2000FormStateEnumState.READ}
            className="h-4 w-4 rounded-sm border-gray-300 text-blue-600 focus:ring-blue-500 disabled:cursor-not-allowed"
            aria-label={t("lblNonBillable", { ns: NS })}
            aria-checked={nonBillable}
            aria-disabled={formStateHandlers.getFormState?.() === U2000FormStateEnumState.READ}
          />
          <span>{t("lblNonBillable", { ns: NS })}</span>
        </label>
        <div className="min-w-40 max-w-48">
          <U2000InputCalendarCustom formStateHandlers={formStateHandlers} fieldName="billingProfile.nonBillableStartDate" readonly />
        </div>
        <div className={`ml-auto min-w-40 max-w-56 ${hourlyMarkupFieldErrorClassName}`.trim()}>
          <U2000InputDecimal
            formStateHandlers={formStateHandlers}
            fieldName="billingProfile.hourlyMarkup"
            label={t("lblHourlyMarkup", { ns: NS })}
            decimalDigits={2}
            allowNegative={false}
            readonly={!billable}
            required={billable}
          />
        </div>
      </div>

      {showBillableModeError && (
        <div className="col-span-full flex items-start gap-1.5 animate-fade-in">
          <FaExclamationTriangle className="w-3.5 h-3.5 text-red-500 shrink-0 mt-0.5" aria-hidden="true" />
          <p className="text-sm text-red-600 leading-tight" role="alert" aria-live="polite">
            {billableModeMessage}
          </p>
        </div>
      )}

      <div className="col-span-full mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <U2000Panel
          className={`${panelErrorClassName} ${fixedAmountPanelErrorClassName}`}
          title={<U2000CheckBox formStateHandlers={formHandlersFixedPriceHourly} fieldName="billingProfile.fixedPrice" label={t("lblFixedPrice", { ns: NS })} readonly={!billable} />}
        >
          <U2000InputCurrency
            formStateHandlers={formStateHandlers}
            fieldName="billingProfile.fixedAmount"
            label={t("lblFixedAmount", { ns: NS })}
            decimalDigits={2}
            allowNegative={false}
            readonly={!fixedPrice}
            required={fixedPrice}
          />
        </U2000Panel>
        <U2000Panel
          className={`${panelErrorClassName} ${hourlyPanelErrorClassName}`}
          title={<U2000CheckBox formStateHandlers={formHandlersFixedPriceHourly} fieldName="billingProfile.hourly" label={t("lblHourly", { ns: NS })} readonly={!billable} />}
        >
          <U2000InputCurrency
            formStateHandlers={formStateHandlers}
            fieldName="billingProfile.sellingRate"
            label={t("lblSellingRate", { ns: NS })}
            decimalDigits={2}
            allowNegative={false}
            readonly={!billable || !hourly}
            required={billable && hourly}
          />
        </U2000Panel>
      </div>

      <div className="col-span-full mt-6">
        <U2000Panel title={t("lblBillingFrequency", { ns: NS })}>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <U2000ComboBox<{ value: U3121BillingFrequency; label: string }>
              formStateHandlers={formHandlersBillingFrequency}
              fieldName="billingProfile.billingFrequency"
              label={t("lblBillingFrequency", { ns: NS })}
              data={billingFrequencyOptions}
              keyField="value"
              descField="label"
              getValue={(item) => item.value}
              findItemByValue={(value, arr) => arr.find((o) => o.value === value) ?? null}
              formatLabel={(item) => item.label}
              readonly={!billable}
              required={billable}
            />
            <U2000InputText formStateHandlers={formStateHandlers} fieldName="billingProfile.billingFrequencyNote" label={t("lblBillingFrequencyNote", { ns: NS })} readonly={!billable || !isByStep} />
          </div>
        </U2000Panel>
      </div>
    </div>
  );
}

export default U3121SubProjectBillingProfileBody;
